// Single source of truth for API paths. Nothing should inline an endpoint
// string at a call site — declare it here and import it.
//
// Paths are relative to the base the calling useAxios instance was created
// with (`base` | `ops` | `brand` | `affiliate`, see themeConfig.apiUrl), so
// each group below names the base it belongs to. Anything parameterised is a
// function, which keeps the shape of the path in one place.

// base — the shared uploads service. Every uploader posts a multipart body here
// with a `type` query param naming the folder the object lands in; the returned
// { url, name, originalName, size } is what a feature then attaches to a record.
export const UPLOAD_ENDPOINT = "/uploads/v1";

// brand — <api>/brands
export const BRAND_ENDPOINTS = {
    DASHBOARD_ACCOUNTS    : "v1/dashboard/accounts",
    // PATCH { payment_model, kyc_window, debit_note_frequency? } — the workspace
    // setup gate. `debit_note_frequency` is omitted for self payout and stored null.
    PAYMENT_SETUP         : (accountId) => `v1/accounts/${accountId}/payment-setup`,
    // Every publisher invoice raised against the brand, newest first, paged.
    // Carries `total_amount` for the whole filtered set alongside the page.
    INVOICES              : (accountId) => `v1/accounts/${accountId}/invoices`,
    // Account-scoped twins of the ops payment actions: a self-payout brand records
    // and reverts its own payouts. PATCH { payment_reference, payment_date, payment_mode }
    MARK_INVOICE_PAID     : (accountId, invoiceId) => `v1/accounts/${accountId}/invoices/${invoiceId}/mark-paid`,
    // PATCH { reason } — undoes a mark-paid
    REVERT_INVOICE_PAYMENT: (accountId, invoiceId) => `v1/accounts/${accountId}/invoices/${invoiceId}/revert-payment`,
    DEBIT_NOTES           : (accountId) => `v1/accounts/${accountId}/debit-notes`,
    // The publisher invoices a debit note settles — its vendor-payout lines.
    DEBIT_NOTE_INVOICES   : (accountId, noteId) => `v1/accounts/${accountId}/debit-notes/${noteId}/invoices`,
    // PATCH { status, reason? }
    DEBIT_NOTE_STATUS     : (accountId, noteId) => `v1/accounts/${accountId}/debit-notes/${noteId}/status`,
    // One row per publisher of the brand — their contract, or a `not_sent`
    // placeholder with `_id: null`. `counts` covers every publisher.
    CONTRACT_PUBLISHERS   : (accountId) => `v1/accounts/${accountId}/contract/publishers`,
    // POST, no body — signs one `awaiting_brand` contract synchronously
    CONTRACT_SIGN         : (accountId, contractId) => `v1/accounts/${accountId}/contract/publishers/${contractId}/sign`,
    // POST, no body — queues every `awaiting_brand` contract for signing
    CONTRACT_SIGN_ALL     : (accountId) => `v1/accounts/${accountId}/contract/sign-all`,
    // The PDF as signed so far
    CONTRACT_DOCUMENT     : (accountId, contractId) => `v1/accounts/${accountId}/contract/publishers/${contractId}/document`
};

// affiliate — <api>/affiliates. "Publisher" and "affiliate" are the same entity;
// the UI says publisher, the API path says affiliates.
export const PUBLISHER_ENDPOINTS = {
    DASHBOARD_ACCOUNTS: "v1/dashboard/accounts",
    // Carries the KYC window alongside the status — `kyc_window_days` and
    // `days_remaining`. 404s while nothing has been submitted yet, which is not
    // an error, so callers ask with showAlert off.
    KYC_DETAILS       : (accountId) => `v1/accounts/${accountId}/kyc/details`,
    // One POST per wizard step, each persisting its own slice of `kyc_docs`.
    KYC_PERSONAL      : (accountId) => `v1/accounts/${accountId}/kyc/personal`,
    KYC_TAX           : (accountId) => `v1/accounts/${accountId}/kyc/tax`,
    KYC_BANK          : (accountId) => `v1/accounts/${accountId}/kyc/bank`,
    // Fired once every step above has been stored — hands the completed set to
    // review. Takes no body; the server reads the docs it already holds.
    KYC_VERIFY        : (accountId) => `v1/accounts/${accountId}/kyc/verify`,
    // The publisher's own contract with this brand. 200 with `contract_id: null`
    // and `status: "not_sent"` until ops has sent it.
    CONTRACT          : (accountId) => `v1/accounts/${accountId}/contract`,
    CONTRACT_DOCUMENT : (accountId) => `v1/accounts/${accountId}/contract/document`,
    // POST, no body — 409 until the brand has signed
    CONTRACT_SIGN     : (accountId) => `v1/accounts/${accountId}/contract/acknowledge`
};

// Endpoints that feed filter dropdowns. Each one is a full path from the API
// root, including the role segment (`ops/`, `brands/`, `affiliates/`), so a
// <Select URL={...}> resolves without also being told which base to use.
export const FILTER_ENDPOINTS = {
    BRANDS: "ops/accounts/v1"
};

// ops — <api>/ops
export const OPS_ENDPOINTS = {
    DISBURSEMENTS         : "disbursements/v1",
    // PATCH { payment_reference, payment_date, payment_mode } on a publisher invoice
    MARK_INVOICE_PAID     : (invoiceId) => `affiliates/v1/${invoiceId}/mark-paid`,
    // PATCH { reason } — undoes a mark-paid
    REVERT_INVOICE_PAYMENT: (invoiceId) => `affiliates/v1/${invoiceId}/revert-payment`,
    BRAND                 : (brandId) => `accounts/v1/${brandId}`,
    // Per-brand stats, per-currency amounts and latest active debit notes. Served from
    // a cache — `?refresh=true` rebuilds it.
    BRAND_DASHBOARD       : (brandId) => `accounts/v1/${brandId}/dashboard`,
    BRAND_PUBLISHERS      : (brandId) => `accounts/v1/${brandId}/affiliate`,
    // POST { holder_id? } — omit holder_id to remind every incomplete publisher of the brand
    BRAND_KYC_REMINDER    : (brandId) => `affiliates/v1/account/${brandId}/kyc-reminder`,
    // Status, per-document details (PAN / GSTIN / bank) and the KYC window countdown
    PUBLISHER_KYC         : (publisherId) => `affiliates/v1/${publisherId}/kyc`,
    PUBLISHER_KYC_VERIFY  : (publisherId) => `affiliates/v1/${publisherId}/kyc/force-verify`,
    PUBLISHER_INVOICES    : (publisherId) => `affiliates/v1/${publisherId}/invoices`,
    // PATCH any of { payment_model, debit_note_frequency, payment_setup_status, kyc_window } —
    // only the fields sent are changed
    BRAND_PAYMENT_SETUP   : (brandId) => `accounts/v1/${brandId}/payment-setup`,
    // Every debit note across all brands
    DEBIT_NOTES           : "debit-notes/v1",
    BRAND_DEBIT_NOTES     : (brandId) => `accounts/v1/${brandId}/debit-notes`,
    DEBIT_NOTE_INVOICES   : (brandId, noteId) => `accounts/v1/${brandId}/debit-notes/${noteId}/invoices`,
    // PATCH { status, reason? }
    DEBIT_NOTE_STATUS     : (brandId, noteId) => `accounts/v1/${brandId}/debit-notes/${noteId}/status`,
    // PATCH { reason } — Ops recall of an issued / acknowledged note
    DEBIT_NOTE_REVERT     : (brandId, noteId) => `accounts/v1/${brandId}/debit-notes/${noteId}/revert`,
    // Every brand, with its denormalised `contract_status`; filterable by it
    BRAND_CONTRACTS       : "accounts/v1",
    // GET { template, template_history, publisher_contracts } ·
    // POST { contract_url, contract_meta } saves the brand's contract template.
    // 409 while any publisher contract of the current version is unsigned.
    BRAND_CONTRACT        : (brandId) => `accounts/v1/${brandId}/contract`,
    // The uploaded template PDF
    BRAND_CONTRACT_FILE   : (brandId) => `accounts/v1/${brandId}/contract/document`,
    CONTRACT_PUBLISHERS   : (brandId) => `accounts/v1/${brandId}/contract/publishers`,
    // POST {} sends to every active publisher not yet sent this version;
    // POST { holder_access_ids } sends to just those.
    CONTRACT_SEND         : (brandId) => `accounts/v1/${brandId}/contract/publishers/send`,
    // POST — re-queues sends and brand signs stuck for over 10 minutes
    CONTRACT_RETRY        : (brandId) => `accounts/v1/${brandId}/contract/publishers/retry`,
    // One publisher's PDF as signed so far
    CONTRACT_DOCUMENT     : (brandId, contractId) => `accounts/v1/${brandId}/contract/publishers/${contractId}/document`
};
